import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGameStore } from '../store/gameStore';
import { getGame, getPublicGame, getTeam } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Trophy, Target, Zap, TrendingUp, Printer, RefreshCw, Download, FileText } from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { usePremium } from '../hooks/usePremium';
import { PremiumBadge } from '../components/shared/PremiumGate';

// Play log entry (shared with ParentView)
const PlayLogEntry = ({ entry, index }) => {
  const isOut = entry.description?.toLowerCase().includes('out') || entry.description?.toLowerCase().includes('strikeout');
  const isScore = entry.description?.includes(' R)') || entry.description?.includes('HOME RUN');
  const isError = entry.description?.toLowerCase().includes('error');
  const isWalk = entry.description?.toLowerCase().includes('walk') || entry.description?.toLowerCase().includes('hbp');
  return (
    <div className={cn(
      'rounded p-2 text-xs border-l-[3px]',
      isOut ? 'border-l-red-500 bg-stone-700/40' : isScore ? 'border-l-green-500 bg-stone-700/40' : isError ? 'border-l-orange-500 bg-stone-700/40' : isWalk ? 'border-l-blue-500 bg-stone-700/40' : 'border-l-stone-600 bg-stone-700/30'
    )}>
      <div className="flex items-start justify-between gap-2">
        <p className={cn('font-semibold', isOut ? 'text-red-300' : isScore ? 'text-green-300' : isError ? 'text-orange-300' : isWalk ? 'text-blue-300' : 'text-stone-200')}>
          {entry.description}
        </p>
        <span className="text-stone-500 text-[10px] whitespace-nowrap">{entry.time}</span>
      </div>
      <div className="flex gap-3 mt-0.5 text-stone-500 text-[10px]">
        <span>{entry.isTop ? '▲' : '▼'}{entry.inning}</span>
        <span>{entry.outs} out{entry.outs !== 1 ? 's' : ''}</span>
        <span>{entry.score}</span>
      </div>
    </div>
  );
};

// Compute player stats from play log
const computePlayerStats = (playLog, battingOrder, players) => {
  const stats = {};
  battingOrder.forEach(id => {
    const player = players?.find(p => p.id === id);
    if (player) {
      stats[id] = {
        name: player.name, jersey: player.jersey_number,
        ab: 0, h: 0, singles: 0, doubles: 0, triples: 0, hr: 0,
        rbi: 0, bb: 0, k: 0, hbp: 0, sac: 0, errors_reached: 0
      };
    }
  });

  playLog?.forEach(entry => {
    const desc = entry.description || '';
    // Extract player name from "Type - PlayerName" pattern
    const match = desc.match(/- (.+?)(?:\s*\(|$)/);
    const playerName = match?.[1]?.trim();
    const playerEntry = Object.values(stats).find(s => s.name === playerName);
    if (!playerEntry) return;
    const id = Object.keys(stats).find(k => stats[k] === playerEntry);
    if (!id) return;

    // Extract runs from "(N R)"
    const runsMatch = desc.match(/\((\d+) R\)/);
    const runs = runsMatch ? parseInt(runsMatch[1]) : 0;

    if (desc.includes('Single')) { stats[id].ab++; stats[id].h++; stats[id].singles++; stats[id].rbi += runs; }
    else if (desc.includes('Double') && !desc.includes('Double Play')) { stats[id].ab++; stats[id].h++; stats[id].doubles++; stats[id].rbi += runs; }
    else if (desc.includes('Triple')) { stats[id].ab++; stats[id].h++; stats[id].triples++; stats[id].rbi += runs; }
    else if (desc.includes('HOME RUN')) { stats[id].ab++; stats[id].h++; stats[id].hr++; stats[id].rbi += runs; }
    else if (desc.includes('Walk')) { stats[id].bb++; }
    else if (desc.includes('Strikeout')) { stats[id].ab++; stats[id].k++; }
    else if (desc.includes('HBP')) { stats[id].hbp++; }
    else if (desc.includes('Sac')) { stats[id].sac++; stats[id].rbi += runs; }
    else if (desc.includes('Error') && desc.includes(playerName)) { stats[id].errors_reached++; stats[id].rbi += runs; }
    else if (desc.includes('Out') && !desc.includes('Pick Off') && desc.includes(playerName)) { stats[id].ab++; }
    else if (desc.includes('Flyout') && desc.includes(playerName)) { stats[id].ab++; }
    else if (desc.includes('Drop 3rd') && desc.includes('Out')) { stats[id].ab++; stats[id].k++; }
  });

  return stats;
};

export default function GameSummaryPage() {
  const navigate = useNavigate();
  const { teamId: paramTeamId, gameId: paramGameId } = useParams();
  const { currentGameId, currentTeamId } = useGameStore();
  const { isPremium } = usePremium();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generatingStory, setGeneratingStory] = useState(false);

  const gameId = paramGameId || currentGameId;
  const teamId = paramTeamId || currentTeamId;

  useEffect(() => {
    if (!gameId || !teamId) { navigate('/'); return; }
    loadData();
  }, [gameId, teamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      let gameData;
      try { gameData = await getGame(gameId); } catch { gameData = await getPublicGame(gameId); }
      const teamData = await getTeam(teamId);
      setGame(gameData);
      setTeam(teamData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900">
        <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!game || !team) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-900 text-white">
        <p>Game not found</p>
      </div>
    );
  }

  const maxInnings = game.sport_type === 'softball' ? 7 : 9;
  const displayInnings = Array.from({ length: Math.max(maxInnings, game.current_inning || 1) }, (_, i) => i + 1);
  const playLog = game.play_log || [];
  const playerStats = computePlayerStats(playLog, game.batting_order || [], team.players);
  const pitchingHistory = game.pitching_history || [];
  const isHome = game.is_home;

  return (
    <div className="min-h-screen bg-stone-900 p-4 md:p-6" data-testid="game-summary-page">
      <div className="max-w-5xl mx-auto space-y-4">

        {/* Header */}
        <div className="flex items-center gap-3 print-hide">
          <Button variant="ghost" className="text-stone-400 hover:text-white" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={16} className="mr-1" /> Back
          </Button>
          <div className="flex-1 text-center">
            <div className="flex items-center justify-center gap-2">
              <Trophy size={20} className="text-amber-400" />
              <h1 className="text-xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                GAME SUMMARY
              </h1>
            </div>
            <p className="text-stone-500 text-xs">{game.date} • {game.season}</p>
          </div>
          <div className="flex items-center gap-2">
            {game.is_complete && (
              <span className="bg-stone-700 text-stone-300 px-3 py-1 rounded text-xs font-semibold">FINAL</span>
            )}
            <Button variant="outline" size="sm" className="text-stone-300 border-stone-600" onClick={() => window.print()} data-testid="print-summary-btn">
              <Printer size={14} className="mr-1" /> Print
            </Button>
            <Button variant="outline" size="sm" className="text-stone-300 border-stone-600"
              data-testid="export-game-csv-btn"
              onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/game-summary/${gameId}`;
                const a = document.createElement('a'); a.href = url; a.download = 'game_summary.csv'; a.click();
              }}>
              <Download size={14} className="mr-1" /> CSV
            </Button>
            <Button variant="outline" size="sm" className="text-stone-300 border-stone-600"
              data-testid="export-play-log-csv-btn"
              onClick={() => {
                const rows = [['#', 'Time', 'Inning', 'Half', 'Outs', 'Score', 'Description']];
                (game.play_log || []).forEach((e, i) => {
                  rows.push([
                    i + 1,
                    e.time || '',
                    e.inning || '',
                    e.isTopInning ? 'TOP' : 'BOT',
                    e.outs ?? '',
                    e.score || '',
                    String(e.description || '').replace(/"/g, '""'),
                  ]);
                });
                const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `play_log_${game.opponent_name || 'game'}_${game.date || ''}.csv`.replace(/\s+/g, '_');
                a.click();
                URL.revokeObjectURL(a.href);
              }}>
              <FileText size={14} className="mr-1" /> Play Log
            </Button>
          </div>
        </div>

        {/* Print-only Header */}
        <div className="hidden print:block text-center mb-4 border-b-2 border-black pb-3">
          <h1 className="text-2xl font-extrabold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            GAME SUMMARY
          </h1>
          <p className="text-sm mt-1">
            {team?.name} {game.is_home ? 'vs' : '@'} {game.opponent_name} • {game.date} • {game.season}
          </p>
          <p className="text-lg font-bold mt-2">
            {game.away_score} - {game.home_score} {game.is_complete ? '(FINAL)' : ''}
          </p>
        </div>

        {/* Final Score */}
        <Card className="border-0 bg-gradient-to-r from-stone-800 to-stone-800/80 shadow-xl overflow-hidden">
          <CardContent className="p-6">
            <div className="flex items-center justify-center gap-8">
              <div className="text-center flex-1">
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">
                  {isHome ? game.opponent_name : team.name}
                </p>
                <p className="text-5xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="away-final-score">
                  {game.away_score}
                </p>
              </div>
              <div className="text-center">
                <p className="text-stone-500 text-lg font-bold">vs</p>
                {game.is_complete && (
                  <p className="text-amber-400 text-xs font-bold mt-1">FINAL</p>
                )}
              </div>
              <div className="text-center flex-1">
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">
                  {isHome ? team.name : game.opponent_name}
                </p>
                <p className="text-5xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="home-final-score">
                  {game.home_score}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Box Score - Inning by Inning */}
        <Card className="border-0 bg-stone-800 shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              BOX SCORE
            </CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="box-score-table">
              <thead>
                <tr className="border-b border-stone-700">
                  <th className="text-left text-stone-400 py-2 px-2 min-w-[100px]">Team</th>
                  {displayInnings.map(inn => (
                    <th key={inn} className="text-center text-stone-400 py-2 px-1 w-8">{inn}</th>
                  ))}
                  <th className="text-center text-stone-300 py-2 px-2 border-l border-stone-700 font-extrabold">R</th>
                  <th className="text-center text-stone-300 py-2 px-2">H</th>
                  <th className="text-center text-stone-300 py-2 px-2">E</th>
                </tr>
              </thead>
              <tbody>
                {/* Away team */}
                <tr className="border-b border-stone-700/50">
                  <td className="text-stone-300 py-2 px-2 font-semibold truncate max-w-[100px]">
                    {isHome ? game.opponent_name : team.name}
                  </td>
                  {displayInnings.map(inn => {
                    const innData = (isHome ? game.opponent_innings : game.innings)?.find(i => i.inning === inn);
                    return <td key={inn} className="text-center text-stone-400 py-2 px-1">{innData?.runs ?? '-'}</td>;
                  })}
                  <td className="text-center text-white py-2 px-2 border-l border-stone-700 font-extrabold">{game.away_score}</td>
                  <td className="text-center text-stone-400 py-2 px-2">{(isHome ? game.opponent_innings : game.innings)?.reduce((s, i) => s + (i.hits || 0), 0) || 0}</td>
                  <td className="text-center text-stone-400 py-2 px-2">{(isHome ? game.opponent_innings : game.innings)?.reduce((s, i) => s + (i.errors || 0), 0) || 0}</td>
                </tr>
                {/* Home team */}
                <tr>
                  <td className="text-stone-300 py-2 px-2 font-semibold truncate max-w-[100px]">
                    {isHome ? team.name : game.opponent_name}
                  </td>
                  {displayInnings.map(inn => {
                    const innData = (isHome ? game.innings : game.opponent_innings)?.find(i => i.inning === inn);
                    return <td key={inn} className="text-center text-stone-400 py-2 px-1">{innData?.runs ?? '-'}</td>;
                  })}
                  <td className="text-center text-white py-2 px-2 border-l border-stone-700 font-extrabold">{game.home_score}</td>
                  <td className="text-center text-stone-400 py-2 px-2">{(isHome ? game.innings : game.opponent_innings)?.reduce((s, i) => s + (i.hits || 0), 0) || 0}</td>
                  <td className="text-center text-stone-400 py-2 px-2">{(isHome ? game.innings : game.opponent_innings)?.reduce((s, i) => s + (i.errors || 0), 0) || 0}</td>
                </tr>
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Game Summary Story */}
        {game.game_summary_story ? (
          <Card className="border-0 bg-gradient-to-br from-stone-800 to-stone-800/80 shadow-xl border-l-4 border-l-amber-500" data-testid="game-summary-story">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-amber-400 text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Trophy size={16} /> GAME RECAP
              </CardTitle>
              <Button size="sm" variant="ghost" className="text-stone-500 hover:text-amber-400 h-7 text-xs"
                disabled={generatingStory}
                onClick={async () => {
                  setGeneratingStory(true);
                  try {
                    const { generateGameSummary } = await import('../lib/api');
                    const result = await generateGameSummary(gameId);
                    setGame(prev => ({ ...prev, game_summary_story: result.story }));
                    toast.success('Recap regenerated!');
                  } catch { toast.error('Failed to regenerate'); }
                  finally { setGeneratingStory(false); }
                }}
                data-testid="regenerate-story-btn">
                <RefreshCw size={12} className={cn("mr-1", generatingStory && "animate-spin")} />
                {generatingStory ? 'Writing...' : 'Regenerate'}
              </Button>
            </CardHeader>
            <CardContent>
              <div className="text-stone-300 text-sm leading-relaxed whitespace-pre-wrap" data-testid="game-story-text">
                {game.game_summary_story}
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 bg-stone-800/60 shadow-xl border border-dashed border-stone-600">
            <CardContent className="py-6 text-center">
              <Trophy size={28} className="mx-auto text-stone-600 mb-3" />
              <p className="text-stone-400 text-sm mb-3">No game recap yet</p>
              <Button
                className="bg-amber-600 hover:bg-amber-500 font-bold"
                disabled={generatingStory || !playLog.length}
                onClick={async () => {
                  setGeneratingStory(true);
                  try {
                    const { generateGameSummary } = await import('../lib/api');
                    const result = await generateGameSummary(gameId);
                    setGame(prev => ({ ...prev, game_summary_story: result.story }));
                    toast.success('Game recap generated!');
                  } catch { toast.error('Failed to generate recap'); }
                  finally { setGeneratingStory(false); }
                }}
                data-testid="generate-story-btn"
              >
                {generatingStory ? (
                  <><RefreshCw size={14} className="mr-2 animate-spin" /> Writing Recap...</>
                ) : (
                  <><Trophy size={14} className="mr-2" /> Generate Game Recap</>
                )}
              </Button>
              {!playLog.length && <p className="text-stone-600 text-xs mt-2">Record some plays first</p>}
            </CardContent>
          </Card>
        )}

        {/* Two column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* Player Stats */}
          <Card className="border-0 bg-stone-800 shadow-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <TrendingUp size={16} className="text-green-400" /> BATTING STATS
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              {Object.keys(playerStats).length === 0 ? (
                <p className="text-stone-500 text-center py-4 text-sm">No batting data</p>
              ) : (
                <table className="w-full text-xs" data-testid="batting-stats-table">
                  <thead>
                    <tr className="border-b border-stone-700">
                      <th className="text-left text-stone-400 py-1.5 px-1">#</th>
                      <th className="text-left text-stone-400 py-1.5 px-1 min-w-[80px]">Player</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">AB</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">H</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">1B</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">2B</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">3B</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">HR</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">RBI</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">BB</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">K</th>
                      <th className="text-center text-stone-400 py-1.5 px-1">AVG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(game.batting_order || []).map((id, idx) => {
                      const s = playerStats[id];
                      if (!s) return null;
                      const avg = s.ab > 0 ? (s.h / s.ab).toFixed(3) : '.000';
                      const hasHits = s.h > 0;
                      const hasHR = s.hr > 0;
                      return (
                        <tr key={id} className={cn('border-b border-stone-700/30', hasHR && 'bg-amber-900/20')}>
                          <td className="text-stone-500 py-1.5 px-1">{s.jersey}</td>
                          <td className="text-stone-200 py-1.5 px-1 font-medium truncate max-w-[80px] cursor-pointer hover:text-green-400 transition-colors" data-testid={`summary-player-link-${id}`} onClick={() => navigate(`/player-profile/${teamId}/${id}`)}>{s.name}</td>
                          <td className="text-center text-stone-300 py-1.5 px-1">{s.ab}</td>
                          <td className={cn('text-center py-1.5 px-1 font-bold', hasHits ? 'text-green-400' : 'text-stone-500')}>{s.h}</td>
                          <td className="text-center text-stone-400 py-1.5 px-1">{s.singles || '-'}</td>
                          <td className="text-center text-stone-400 py-1.5 px-1">{s.doubles || '-'}</td>
                          <td className="text-center text-stone-400 py-1.5 px-1">{s.triples || '-'}</td>
                          <td className={cn('text-center py-1.5 px-1', hasHR ? 'text-amber-400 font-bold' : 'text-stone-400')}>{s.hr || '-'}</td>
                          <td className={cn('text-center py-1.5 px-1', s.rbi > 0 ? 'text-green-300 font-bold' : 'text-stone-400')}>{s.rbi || '-'}</td>
                          <td className="text-center text-stone-400 py-1.5 px-1">{s.bb || '-'}</td>
                          <td className="text-center text-stone-400 py-1.5 px-1">{s.k || '-'}</td>
                          <td className={cn('text-center py-1.5 px-1 font-mono', parseFloat(avg) >= 0.300 ? 'text-green-400 font-bold' : 'text-stone-300')}>{avg}</td>
                        </tr>
                      );
                    })}
                    {/* Totals */}
                    <tr className="border-t-2 border-stone-600 font-bold">
                      <td className="py-1.5 px-1" colSpan={2}><span className="text-stone-300">TOTALS</span></td>
                      <td className="text-center text-stone-200 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.ab, 0)}</td>
                      <td className="text-center text-green-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.h, 0)}</td>
                      <td className="text-center text-stone-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.singles, 0)}</td>
                      <td className="text-center text-stone-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.doubles, 0)}</td>
                      <td className="text-center text-stone-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.triples, 0)}</td>
                      <td className="text-center text-amber-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.hr, 0)}</td>
                      <td className="text-center text-green-300 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.rbi, 0)}</td>
                      <td className="text-center text-stone-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.bb, 0)}</td>
                      <td className="text-center text-stone-400 py-1.5 px-1">{Object.values(playerStats).reduce((s, p) => s + p.k, 0)}</td>
                      <td className="text-center text-stone-300 py-1.5 px-1 font-mono">
                        {(() => { const t = Object.values(playerStats); const ab = t.reduce((s, p) => s + p.ab, 0); const h = t.reduce((s, p) => s + p.h, 0); return ab > 0 ? (h / ab).toFixed(3) : '.000'; })()}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>

          {/* Pitching Summary */}
          <Card className="border-0 bg-stone-800 shadow-xl">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <Target size={16} className="text-amber-400" /> PITCHING SUMMARY
              </CardTitle>
            </CardHeader>
            <CardContent>
              {pitchingHistory.length === 0 && !game.current_pitcher_id ? (
                <p className="text-stone-500 text-center py-4 text-sm">No pitching data</p>
              ) : (
                <div className="space-y-3" data-testid="pitching-summary">
                  {/* Current pitcher (if game still active) */}
                  {game.current_pitcher_id && (
                    <div className="bg-green-900/30 border border-green-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded bg-amber-600 text-white flex items-center justify-center font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                            {team.players?.find(p => p.id === game.current_pitcher_id)?.jersey_number || '?'}
                          </div>
                          <div>
                            <p className="text-white font-semibold">{team.players?.find(p => p.id === game.current_pitcher_id)?.name || 'Unknown'}</p>
                            <p className="text-green-400 text-xs">{!game.is_complete ? 'Currently Pitching' : 'Final Pitcher'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-extrabold text-white" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{game.pitch_count || 0}</p>
                          <p className="text-stone-400 text-[10px] uppercase">Pitches</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Pitching history */}
                  {pitchingHistory.length > 0 && (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-stone-700">
                          <th className="text-left text-stone-400 py-1.5">Pitcher</th>
                          <th className="text-center text-stone-400 py-1.5">Inn</th>
                          <th className="text-center text-stone-400 py-1.5">Pitches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pitchingHistory.map((entry, idx) => (
                          <tr key={idx} className="border-b border-stone-700/30">
                            <td className="text-stone-200 py-1.5 font-medium">{entry.pitcher_name}</td>
                            <td className="text-center text-stone-400 py-1.5">
                              {entry.innings_start}{entry.innings_end !== entry.innings_start ? `-${entry.innings_end}` : ''}
                            </td>
                            <td className="text-center py-1.5">
                              <span className="bg-amber-600/30 text-amber-300 px-2 py-0.5 rounded font-bold">{entry.pitch_count}</span>
                            </td>
                          </tr>
                        ))}
                        {/* Total */}
                        <tr className="border-t-2 border-stone-600">
                          <td className="text-stone-300 py-1.5 font-bold">TOTAL</td>
                          <td></td>
                          <td className="text-center py-1.5">
                            <span className="text-amber-400 font-extrabold">
                              {pitchingHistory.reduce((s, e) => s + (e.pitch_count || 0), 0) + (game.pitch_count || 0)}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Player Report Cards (PDF) */}
        {Object.keys(playerStats).length > 0 && (
          <Card className="border-0 bg-stone-800 shadow-xl" data-testid="report-cards-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                <FileText size={16} className="text-amber-400" /> PLAYER REPORT CARDS
                {!isPremium && <PremiumBadge />}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-stone-500 text-xs mb-3">Download a per-player PDF report card with stats, at-bats, and spray data</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {(game.batting_order || []).map(id => {
                  const s = playerStats[id];
                  if (!s) return null;
                  const avg = s.ab > 0 ? (s.h / s.ab).toFixed(3) : '.000';
                  return (
                    <button key={id} data-testid={`report-card-${id}`}
                      onClick={() => {
                        if (!isPremium) { navigate('/upgrade?feature=PDF%20Player%20Report%20Cards'); return; }
                        const url = `${process.env.REACT_APP_BACKEND_URL}/api/export/player-report/${gameId}/${id}`;
                        const a = document.createElement('a'); a.href = url; a.download = `${s.name}_report.pdf`; a.click();
                        toast.success(`Downloading ${s.name}'s report card`);
                      }}
                      className={cn(
                        "flex items-center gap-2 p-2 rounded-lg border transition-all text-left",
                        isPremium
                          ? "bg-stone-700/50 hover:bg-stone-700 border-stone-600 hover:border-amber-600"
                          : "bg-stone-800/60 hover:bg-stone-800 border-stone-700/60"
                      )}>
                      <div className="w-8 h-8 rounded bg-stone-600 flex items-center justify-center text-white font-bold text-xs shrink-0" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                        {s.jersey || '?'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className={cn("text-xs font-semibold truncate", isPremium ? "text-white" : "text-stone-300")}>{s.name}</p>
                        <p className="text-stone-500 text-[9px]">{s.h}-{s.ab} {avg}</p>
                      </div>
                      {isPremium
                        ? <Download size={12} className="text-amber-400 shrink-0" />
                        : <svg className="shrink-0 text-amber-500" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M17 8V7a5 5 0 0 0-10 0v1H5v14h14V8h-2zm-8-1a3 3 0 1 1 6 0v1H9V7z"/></svg>
                      }
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Play-by-Play Log */}
        <Card className="border-0 bg-stone-800 shadow-xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-white text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              <Zap size={16} className="text-green-400" /> PLAY-BY-PLAY ({playLog.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {playLog.length === 0 ? (
              <p className="text-stone-500 text-center py-6 text-sm">No plays recorded</p>
            ) : (
              <div className="space-y-1.5 max-h-[500px] overflow-y-auto" data-testid="summary-play-log">
                {[...playLog].reverse().map((entry, idx) => (
                  <PlayLogEntry key={entry.id || idx} entry={entry} index={idx} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
